import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { filterProps } from 'utils/helpers';

interface ISuggestionChips {
  chips: string[];
  onChipClick: (chip: string) => void;
  disabled?: boolean;
}

const OuterContainer = styled('div')`
  overflow-x: hidden;
  overflow-y: visible;
  width: 100%;
`;

const Container = styled('div')`
  ${({ theme: { mq } }) => css`
    display: flex;
    gap: 0.75rem;
    overflow-x: auto;
    overflow-y: visible;
    padding: 1.5rem 0;
    margin: -0.5rem 0;
    -webkit-overflow-scrolling: touch;
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* Add padding to first and last child for edge spacing */
    & > *:first-of-type {
      margin-left: 3rem;
    }
    
    & > *:last-child {
      margin-right: 3rem;
    }

    ${mq.md.down} {
      gap: 0.5rem;
      padding: 1.5rem 0;
      
      & > *:first-of-type {
        margin-left: 2rem;
      }
      
      & > *:last-child {
        margin-right: 2rem;
      }
    }
  `}
`;

const Chip = styled('button')<{ disabled?: boolean }>`
  ${({ theme: { colors, durations, tp, utils }, disabled }) => css`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1rem 1.5rem;
    border: none;
    border-radius: 16px;
    background-color: ${colors.neutral.white};
    color: ${colors.primary.dark};
    font-size: 14px;
    line-height: 1.25;
    font-weight: 600;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    transition: all ${durations.regular};
    white-space: nowrap;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    box-shadow: 0px 2px 12px rgba(43, 20, 59, 0.08);
    
    &:hover:not(:disabled) {
      background-color: ${colors.neutral.white};
      color: ${colors.primary.main};
      box-shadow: 0px 4px 16px rgba(43, 20, 59, 0.12);
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      background-color: ${utils.alphaHex(colors.primary.light, 10)};
      transform: scale(0.98) translateY(0);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &:focus {
      outline: none;
    }
    
    &:focus-visible {
      outline: 2px solid ${colors.primary.light};
      outline-offset: 2px;
    }
  `}
`;

const SuggestionChips: React.FC<ISuggestionChips> = ({ chips, onChipClick, disabled = false }) => {
  if (!chips.length) {
    return null;
  }

  const handleChipClick = (chip: string) => {
    if (!disabled) {
      onChipClick(chip);
    }
  };

  return (
    <OuterContainer>
      <Container data-testid="chips-container">
        {chips.map((chip, index) => (
          <Chip
            key={`${chip}-${index}`}
            onClick={() => handleChipClick(chip)}
            disabled={disabled}
            role="button"
            aria-label={`Select suggestion: ${chip}`}
            aria-disabled={disabled}
          >
            {chip}
          </Chip>
        ))}
      </Container>
    </OuterContainer>
  );
};

export default SuggestionChips;
